import {
  getCachedSegmentPsd,
  isSegmentPsdCached,
} from '../../../shared/spectral';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  filterSessionEventsForSegment,
  getWorkspaceAvailableSeconds,
  resolveSegmentBounds,
} from './resolveSpectraSegment';

export const buildSpectraWarmKey = ({
  readySessions,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
  selectedSessionId,
}: {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  selectedSessionId: string | null;
}) => {
  const fingerprints = readySessions
    .map(session => `${session.sessionId}:${session.events?.length ?? 0}`)
    .join('|');

  return [
    fingerprints,
    activeViewId,
    customSegments
      .map(
        segment => `${segment.id}:${segment.startSecond}:${segment.endSecond}`
      )
      .join('|'),
    trendStart ?? '',
    trendEnd ?? '',
    selectedSessionId ?? 'group',
  ].join('::');
};

export const resolveSpectraWarmTargets = ({
  readySessions,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
  selectedSessionId,
}: {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  selectedSessionId: string | null;
}) => {
  const availableSeconds = getWorkspaceAvailableSeconds(readySessions);
  const segmentBounds = resolveSegmentBounds({
    activeViewId,
    customSegments,
    availableSeconds,
    trendStart,
    trendEnd,
  });

  if (!segmentBounds) {
    return { segmentBounds: null, sessions: [] as SpectralWorkspaceSession[] };
  }

  const sessions = selectedSessionId
    ? readySessions.filter(
        session => session.sessionId === Number(selectedSessionId)
      )
    : readySessions;

  return { segmentBounds, sessions };
};

export const warmSpectraPsdForSession = (
  session: SpectralWorkspaceSession,
  segmentBounds: { startSecond: number; endSecond: number }
) => {
  const segmentEvents = filterSessionEventsForSegment(
    session.events ?? [],
    segmentBounds.startSecond,
    segmentBounds.endSecond
  );

  return getCachedSegmentPsd(
    session.sessionId,
    segmentBounds.startSecond,
    segmentBounds.endSecond,
    segmentEvents
  );
};

export const isSpectraPsdCachedForSession = (
  session: SpectralWorkspaceSession,
  segmentBounds: { startSecond: number; endSecond: number }
) => {
  const segmentEvents = filterSessionEventsForSegment(
    session.events ?? [],
    segmentBounds.startSecond,
    segmentBounds.endSecond
  );

  return isSegmentPsdCached(
    session.sessionId,
    segmentBounds.startSecond,
    segmentBounds.endSecond,
    segmentEvents.length
  );
};
